/**
 * <bfg-buy-box> — Bundles + Free Gift buy box.
 *
 * Owns the subscription toggle, the displayed prices, and the add-to-cart payload.
 * The button itself belongs to the `buy_buttons` block; this component intercepts
 * that form's submit and posts its own /cart/add.js body, the same way
 * norse-bundles-buy-box.js does.
 *
 * The free gift is added as a NESTED cart line (`parent_id`), not as a second
 * independent line. Shopify then removes the gift automatically when the trigger
 * line is removed, and renders it indented in cart, checkout, emails and admin —
 * so there is deliberately no JS here that watches the cart to remove gifts.
 * See https://shopify.dev/docs/apps/build/product-merchandising/nested-cart-lines
 *
 * What Shopify does NOT propagate is a quantity change: lowering the parent's
 * quantity in the cart leaves every child sitting at the quantity it was added
 * with. assets/bfg-bundle-guard.js fixes that up, reading the parent-child
 * structure from Shopify's own `parent_relationship.parent_key`; the only thing it
 * needs from here is `_lp_unit`, the per-bundle quantity of each line. Keep that
 * key in step with that file.
 *
 * One instance per gender is on the page. Only the instance matching
 * <html data-norse-gender> acts; the others stay inert but keep their toggle in
 * sync so switching gender never silently changes purchase type.
 */
(function () {
  'use strict';

  if (customElements.get('bfg-buy-box')) return;

  const BUNDLE_SIZE_KEY = '_lp_items';
  const GROUP_KEY = '_lp_group';
  const UNIT_KEY = '_lp_unit';

  const SELECTORS = {
    price: '[data-bfg-price]',
    compare: '[data-bfg-compare]',
    savings: '[data-bfg-savings]',
    benefits: '[data-bfg-benefits]',
    toggle: '[data-bfg-toggle]',
    lines: '[data-bfg-lines]',
    submitPrice: '.product-form__submit-price',
  };

  /** Matches Liquid's `money_without_trailing_zeros` so the JS repaint never shifts the layout. */
  function formatMoney(cents) {
    const rounded = Math.round(Number(cents) || 0);
    const isWhole = rounded % 100 === 0;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const formatted = window.Shopify.formatMoney(rounded);
      return isWhole ? formatted.replace(/[.,]00(?!\d)/, '') : formatted;
    }

    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: isWhole ? 0 : 2,
      }).format(rounded / 100);
    } catch (error) {
      return `${(rounded / 100).toFixed(isWhole ? 0 : 2)}`;
    }
  }

  function activeGender() {
    return document.documentElement.dataset.norseGender || 'female';
  }

  customElements.define(
    'bfg-buy-box',
    class BfgBuyBox extends HTMLElement {
      connectedCallback() {
        this.priceEl = this.querySelector(SELECTORS.price);
        this.compareEl = this.querySelector(SELECTORS.compare);
        this.savingsEl = this.querySelector(SELECTORS.savings);
        this.benefitsEl = this.querySelector(SELECTORS.benefits);
        this.toggleEl = this.querySelector(SELECTORS.toggle);

        this.lines = this._readLines();
        this.subCents = Number(this.dataset.subCents) || 0;
        this.oneTimeCents = Number(this.dataset.onetimeCents) || 0;
        this.anchorCents = Number(this.dataset.anchorCents) || 0;
        this.hasPlan = this.dataset.hasPlan === 'true';

        this._onToggle = this._onToggle.bind(this);
        this._onGenderChange = this._onGenderChange.bind(this);
        this._onDocumentSubmit = this._onDocumentSubmit.bind(this);

        this.toggleEl?.addEventListener('change', this._onToggle);
        document.addEventListener('norse:gender-change', this._onGenderChange);
        document.addEventListener('submit', this._onDocumentSubmit, true);

        this._render();
        if (this.isActive) this._syncForm();
      }

      disconnectedCallback() {
        this.toggleEl?.removeEventListener('change', this._onToggle);
        document.removeEventListener('norse:gender-change', this._onGenderChange);
        document.removeEventListener('submit', this._onDocumentSubmit, true);
      }

      /**
       * The bundle's lines, server-rendered as JSON because a bundle can hold any
       * number of paid products and gifts — one data attribute per id does not scale.
       */
      _readLines() {
        const node = this.querySelector(SELECTORS.lines);
        if (!node) return { paid: [], gifts: [] };
        try {
          const parsed = JSON.parse(node.textContent || '{}');
          return {
            paid: Array.isArray(parsed.paid) ? parsed.paid : [],
            gifts: Array.isArray(parsed.gifts) ? parsed.gifts : [],
          };
        } catch (error) {
          console.error('bfg-buy-box: could not read cart lines', error);
          return { paid: [], gifts: [] };
        }
      }

      /** The line the sticky bar and anything else reading the form should see. */
      get variantId() {
        return this.lines.paid[0]?.id ?? 0;
      }

      get planId() {
        return this.lines.paid[0]?.selling_plan ?? 0;
      }

      get isActive() {
        return (this.dataset.gender || 'female') === activeGender();
      }

      get isSubscription() {
        return Boolean(this.hasPlan && this.toggleEl?.checked);
      }

      get unitPrice() {
        return this.isSubscription ? this.subCents : this.oneTimeCents;
      }

      get form() {
        return document.getElementById(this.dataset.formId);
      }

      get submitButton() {
        return document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
      }

      get quantity() {
        const input = this.form?.querySelector('[name="quantity"]');
        const parsed = Number(input?.value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
      }

      /* ---------------------------------------------------------------- render */

      _render() {
        const unit = this.unitPrice;
        const savings = Math.max(this.anchorCents - unit, 0);

        if (this.priceEl) this.priceEl.textContent = formatMoney(unit);

        if (this.compareEl) {
          this.compareEl.textContent = formatMoney(this.anchorCents);
          this.compareEl.hidden = savings <= 0;
        }

        if (this.savingsEl) {
          const template = this.dataset.savingsMessage || '';
          this.savingsEl.textContent = template.replace('{amount}', formatMoney(savings));
          this.savingsEl.hidden = savings <= 0 || !template;
        }

        if (this.benefitsEl) this.benefitsEl.hidden = !this.isSubscription;

        // Drives the price colour in CSS — green while subscribing, plain otherwise.
        this.toggleAttribute('data-subscription', this.isSubscription);
      }

      /**
       * Publishes state to the shared product form so anything reading it — the
       * sticky add-to-cart bar above all — sees the gender and purchase type the
       * shopper actually chose.
       */
      _syncForm() {
        const form = this.form;
        if (!form) return;

        const variantInput = form.querySelector('input[name="id"]');
        if (variantInput && this.variantId) variantInput.value = String(this.variantId);

        let planInput = form.querySelector('input[name="selling_plan"]');
        if (!planInput) {
          planInput = document.createElement('input');
          planInput.type = 'hidden';
          planInput.name = 'selling_plan';
          form.appendChild(planInput);
        }
        planInput.value = this.isSubscription ? String(this.planId) : '';

        const submitPrice = this.submitButton?.querySelector(SELECTORS.submitPrice);
        if (submitPrice) submitPrice.textContent = formatMoney(this.unitPrice * this.quantity);

        document.dispatchEvent(
          new CustomEvent('norse:bfg-change', {
            bubbles: true,
            detail: {
              gender: this.dataset.gender,
              sectionId: this.dataset.sectionId,
              variantId: this.variantId,
              sellingPlanId: this.isSubscription ? this.planId : 0,
              priceCents: this.unitPrice,
            },
          })
        );
      }

      /* --------------------------------------------------------------- events */

      _onToggle() {
        this._render();

        // Mirror onto the other gender so switching never flips purchase type.
        if (!this._mirroring) {
          const checked = Boolean(this.toggleEl?.checked);
          document.querySelectorAll('bfg-buy-box').forEach((peer) => {
            if (peer === this || peer.dataset.sectionId !== this.dataset.sectionId) return;
            if (!peer.toggleEl || peer.toggleEl.checked === checked) return;
            peer._mirroring = true;
            peer.toggleEl.checked = checked;
            peer._render();
            peer._mirroring = false;
          });
        }

        if (this.isActive) this._syncForm();
      }

      _onGenderChange() {
        if (this.isActive) this._syncForm();
      }

      _onDocumentSubmit(event) {
        if (!this.isActive) return;

        const form = event.target;
        if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;
        if (form !== this.form) return;

        // Nothing addable: let <product-form> handle it so the shopper still gets
        // the theme's normal error handling instead of a dead button.
        if (!this.variantId) return;

        // Capture phase on document, so this runs before <product-form>'s own
        // listener on the form and stops it posting a second, gift-less line.
        event.preventDefault();
        event.stopImmediatePropagation();

        // A double click, or the sticky bar firing while the main button is still
        // in flight, must be swallowed here — returning early would let the repeat
        // fall through to <product-form> and add the trigger twice.
        if (this._submitting) return;

        this._addToCart();
      }

      /* ----------------------------------------------------------------- cart */

      _buildItems() {
        const { paid, gifts } = this.lines;
        if (!paid.length) return [];

        const key = (this.dataset.linePropertyKey || '').trim();
        const value = (this.dataset.linePropertyValue || '').trim();
        const giftKey = (this.dataset.giftPropertyKey || '').trim();
        const giftValue = (this.dataset.giftPropertyValue || '').trim();

        // A quantity selector, when the page has one, multiplies the whole bundle.
        const multiplier = this.quantity;

        // parent_id references the PARENT'S VARIANT ID within this same request.
        // EVERY other line — the rest of the bundle as well as the gifts — hangs off
        // ONE parent, because the bundle's per-item selling plans are only valid as a
        // set: leaving one product behind would sell it at bundle pricing on its own.
        // Removing the parent makes Shopify drop the whole bundle, and the cart hides
        // the remove control on the children so that is the only way out.
        //
        // The most expensive line is the parent. It is the one a shopper reads as
        // "the product" and the one they are least likely to hunt for a way to drop,
        // and it keeps the cheap items from being removable on their own.
        //
        // Cart Transform's linesMerge would be the native way to model all this, but
        // Shopify rejects that operation outright when a selling plan is present.
        const priceOf = (line) =>
          (this.isSubscription && line.selling_plan ? line.subPrice : line.price) ?? 0;
        const parent = paid.reduce((a, b) => (priceOf(b) > priceOf(a) ? b : a), paid[0]);
        const parentId = parent.id;

        // Group key for the cart-side quantity sync. Scoped by the merchant's source
        // value as well as the parent variant, so two bundles that happen to share a
        // parent product are never mistaken for one group. The purchase type is in
        // there too: the same bundle bought one-time and on subscription is two
        // bundles with two parents, and gifts carry no selling plan of their own, so
        // without it their lines would merge and answer to only one of the parents.
        const groupId = `${value || 'bfg'}:${parentId}:${this.isSubscription ? 'sub' : 'onetime'}`;

        /**
         * The properties every line of this bundle carries, paid or gift.
         *
         * `unit` is how many of this product ONE bundle contains — the quantity
         * before the quantity selector multiplies it. bfg-bundle-guard.js divides the
         * parent's cart quantity by the parent's own unit to learn how many bundles
         * were paid for, then holds every other line at `unit x bundles`. Without it
         * a shopper could add ten bundles, drop the parent back to one in the cart and
         * walk away with ten free gifts.
         */
        const bundleProperties = (unit, role) => {
          const properties = { [GROUP_KEY]: groupId, [UNIT_KEY]: String(unit) };
          if (key) {
            properties[key] = value;
            // How many PAID lines this bundle is meant to have. assets/bfg-bundle-guard.js
            // reads it back to spot a bundle the shopper has broken apart, since the
            // per-item selling plans are only valid when the whole set is bought. Gifts
            // are not part of that count, so they do not carry it.
            if (role !== 'gift') properties[BUNDLE_SIZE_KEY] = String(paid.length);
          }
          return properties;
        };

        const items = paid.map((line) => {
          const unit = line.quantity || 1;
          const item = {
            id: line.id,
            quantity: unit * multiplier,
            properties: bundleProperties(unit, line === parent ? 'parent' : 'child'),
          };
          // An item with no plan of its own stays a one-time purchase even while the
          // toggle is on — bundles mix subscribable and non-subscribable products.
          if (this.isSubscription && line.selling_plan) item.selling_plan = line.selling_plan;
          if (line !== parent) item.parent_id = parentId;
          return item;
        });

        gifts.forEach((gift) => {
          const unit = gift.quantity || 1;
          // Gifts are multiplied like the paid components. Adding them unmultiplied
          // would leave the cart in a state the guard immediately "corrects" by
          // inflating them, and two clicks of add-to-cart already yield two gifts, so
          // a quantity of two has to mean the same thing.
          const properties = bundleProperties(unit, 'gift');
          // Tagged with the source as well so bfg-bundle-guard.js can sweep a gift
          // that outlives its parent. The discount function checks _free_gift first,
          // so this never makes the gift look like a paid trigger line.
          if (giftKey) properties[giftKey] = giftValue;
          items.push({ id: gift.id, quantity: unit * multiplier, parent_id: parentId, properties });
        });

        return items;
      }

      _setLoading(isLoading) {
        const button = this.submitButton;
        if (!button) return;
        button.classList.toggle('loading', isLoading);
        if (isLoading) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
        button.closest('product-form')?.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
      }

      async _addToCart() {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items: this._buildItems() };

        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        }

        this._submitting = true;
        this._setLoading(true);

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await response.json();

          if (data.status) {
            this._handleError(data);
            return;
          }

          if (!cart) {
            window.location = window.routes?.cart_url || '/cart';
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bfg-buy-box',
              productVariantId: String(this.variantId),
              cartData: data,
            });
          }

          cart.renderContents(data);
        } catch (error) {
          console.error('bfg-buy-box: add to cart failed', error);
          this._handleError({ description: null });
        } finally {
          this._submitting = false;
          this._setLoading(false);
        }
      }

      _handleError(data) {
        const productForm = this.form?.closest('product-form');
        if (productForm && typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage(data?.description || undefined);
          return;
        }

        const target = this.form?.querySelector('.product-form__error-message');
        if (target) {
          target.textContent = data?.description || '';
          target.closest('.product-form__error-message-wrapper')?.removeAttribute('hidden');
        }
      }
    }
  );
})();
